// src/types/Tour.ts

import { ScheduleItem, TemplateVariables } from '../utils/scheduleTemplates';

// Enums for consistent values
export enum TourDayType {
  TRAVEL_PERFORMANCE = 'travel_performance', // Travel to city + perform same day (most common)
  TRAVEL_ONLY = 'travel_only',               // Long distance travel, perform next day
  PERFORMANCE_ONLY = 'performance_only',     // Already in city, just perform
  REST = 'rest',                             // Free day
  LOAD_IN = 'load_in'                        // Equipment setup day before show
}

export enum TransportType {
  FLIGHT = 'flight',
  BUS = 'bus',
  TRAIN = 'train',
  RENTAL_CAR = 'rental_car',
  OWN_CAR = 'own_car',
  TAXI = 'taxi',
  FERRY = 'ferry',
  MIXED = 'mixed'
}

export enum AccommodationStandard {
  BUDGET = 'budget',
  MID_RANGE = 'mid_range',
  LUXURY = 'luxury',
  HOSTEL = 'hostel',
  SELF_CATERING = 'self_catering',
  AIRBNB = 'airbnb'
}

export enum MealType {
  VENUE_CATERING = 'venue_catering',     // Venue provides catering
  VENUE_RESTAURANT = 'venue_restaurant', // Venue has on-site restaurant
  BUY_OUT = 'buy_out',                   // Cash payment instead of meal
  PER_DIEM = 'per_diem',                 // Daily allowance
  LOCAL_RESTAURANT = 'local_restaurant',  // External restaurant booking
  PROMOTER_PROVIDED = 'promoter_provided', // Promoter arranges meal
  SELF_CATERED = 'self_catered'          // Artist arranges own meal
}

// Contact structure for venues
export interface TourContact {
  name: string;
  email?: string;
  phone?: string;
}

// Individual venue definition
export interface TourVenue {
  venueNumber: number; // 1-14
  name: string;
  address: string;
  city: string;
  country?: string;
  performanceDate: string; // ISO date string
  performanceTime: string; // HH:MM format
  soundcheckTime?: string; // HH:MM format
  
  // Venue contacts - comprehensive list
  primaryContact: TourContact;
  technicalContact: TourContact;
  artistLiaison?: TourContact;
  transportContact?: TourContact;
  hotelContact?: TourContact;
  cateringContact?: TourContact;
  securityContact?: TourContact;
  merchandisingContact?: TourContact;
  promoterContact?: TourContact;
  productionContact?: TourContact;
  marketingContact?: TourContact;
  
  // Venue specifics
  venueCapacity?: number;
  loadInTime?: string;
  curfewTime?: string;
  
  // Meal arrangements
  lunchType: MealType;
  lunchLocation?: string;
  lunchBudget?: string;
  dinnerType: MealType;
  dinnerLocation?: string;
  
  // Accommodation
  hotelName?: string;
  hotelAddress?: string;
  specialArrangements?: string;
}

// Tour day structure
export interface TourDay {
  dayNumber: number;
  date: Date;
  dayType: TourDayType;
  location: string; // City name
  venue?: TourVenue;
  items: ScheduleItem[];
  notes?: string;
  travelInfo?: {
    fromCity: string;
    toCity: string;
    transportType: TransportType;
    departureTime?: string;
    arrivalTime?: string;
    duration?: number; // hours
  };
}

// Progressive disclosure flags - saved to database for consistency
export interface TourContactFlags {
  hasArtistLiaison: boolean;
  hasTransportContact: boolean;
  hasHotelContact: boolean;
  hasCateringContact: boolean;
  hasSecurityContact: boolean;
  hasMerchandisingContact: boolean;
  hasPromoterContact: boolean;
  hasProductionContact: boolean;
  hasMarketingContact: boolean;
  hasSpecialArrangements: boolean;
}

export interface TourUIFlags {
  hasTourManager: boolean;
  hasProductionManager: boolean;
  numberOfShows: number;
  venueFlags: Record<number, TourContactFlags>; // venue number -> flags
}

// Main tour variables interface combining existing TemplateVariables fields
export interface TourVariables {

  ARTIST: string;
  VENUE: string;
  VENUE_ADDRESS?: string;
  DATE?: string;
  CONTACT_NAME?: string;
  CONTACT_EMAIL?: string;
  CONTACT_PHONE?: string;
  ARRIVAL_DATE?: string;
  DEPARTURE_DATE?: string;
  
  // Tour Overview
  TOUR_NAME: string;
  TOUR_DURATION: string; // number of days
  NUMBER_OF_SHOWS: string; // number of venues
  TOUR_START_DATE: string; // ISO date
  TOUR_END_DATE: string; // ISO date
  
  // Tour Team (conditional based on UI flags)
  HAS_TOUR_MANAGER: boolean;
  TOUR_MANAGER: string;
  TOUR_MANAGER_PHONE: string;
  TOUR_MANAGER_EMAIL: string;
  
  HAS_PRODUCTION_MANAGER: boolean;
  PRODUCTION_MANAGER: string;
  PRODUCTION_MANAGER_PHONE: string;
  PRODUCTION_MANAGER_EMAIL: string;
  
  // Global Tour Settings
  ACCOMMODATION_STANDARD: AccommodationStandard;
  HOTEL_CHAIN_PREFERENCE: string;
  TOUR_DAILY_ALLOWANCE: string; // per diem amount
  
  // Performance Standards (defaults for all venues)
  DEFAULT_SOUNDCHECK_DURATION: string; // minutes
  DEFAULT_PERFORMANCE_DURATION: string; // minutes
  DEFAULT_LOAD_IN_DURATION: string; // minutes
  DEFAULT_LOAD_OUT_DURATION: string; // minutes
  
  // Travel Settings
  TRAVEL_BUFFER_TIME: string; // extra time for delays
  DEFAULT_CHECK_IN_TIME: string; // hotel check-in
  DEFAULT_CHECK_OUT_TIME: string; // hotel check-out
  
  // Store UI flags in database for consistency across sessions
  UI_FLAGS: TourUIFlags;
  
  // ========== ALL 14 VENUES WITH COMPLETE FIELD DEFINITIONS ==========
  
  // Venue 1
  VENUE_1_NAME: string;
  VENUE_1_ADDRESS: string;
  VENUE_1_CITY: string;
  VENUE_1_PERFORMANCE_DATE: string;
  VENUE_1_PERFORMANCE_TIME: string;
  VENUE_1_SOUNDCHECK_TIME: string;
  VENUE_1_PRIMARY_CONTACT_NAME: string;
  VENUE_1_PRIMARY_CONTACT_EMAIL: string;
  VENUE_1_PRIMARY_CONTACT_PHONE: string;
  VENUE_1_TECH_CONTACT_NAME: string;
  VENUE_1_TECH_CONTACT_EMAIL: string;
  VENUE_1_TECH_CONTACT_PHONE: string;
  VENUE_1_ARTIST_LIAISON_NAME: string;
  VENUE_1_ARTIST_LIAISON_EMAIL: string;
  VENUE_1_ARTIST_LIAISON_PHONE: string;
  VENUE_1_TRANSPORT_CONTACT_NAME: string;
  VENUE_1_TRANSPORT_CONTACT_PHONE: string;
  VENUE_1_HOTEL_CONTACT_NAME: string;
  VENUE_1_HOTEL_CONTACT_PHONE: string;
  VENUE_1_CATERING_CONTACT_NAME: string;
  VENUE_1_CATERING_CONTACT_PHONE: string;
  VENUE_1_SECURITY_CONTACT_NAME: string;
  VENUE_1_SECURITY_CONTACT_PHONE: string;
  VENUE_1_MERCHANDISING_CONTACT_NAME: string;
  VENUE_1_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_1_PROMOTER_CONTACT_NAME: string;
  VENUE_1_PROMOTER_CONTACT_EMAIL: string;
  VENUE_1_PROMOTER_CONTACT_PHONE: string;
  VENUE_1_PRODUCTION_CONTACT_NAME: string;
  VENUE_1_PRODUCTION_CONTACT_PHONE: string;
  VENUE_1_MARKETING_CONTACT_NAME: string;
  VENUE_1_MARKETING_CONTACT_EMAIL: string;
  VENUE_1_LUNCH_TYPE: string;
  VENUE_1_LUNCH_LOCATION: string;
  VENUE_1_LUNCH_BUDGET: string;
  VENUE_1_DINNER_TYPE: string;
  VENUE_1_DINNER_LOCATION: string;
  VENUE_1_HOTEL_NAME: string;
  VENUE_1_HOTEL_ADDRESS: string;

  // Venue 2
  VENUE_2_NAME: string;
  VENUE_2_ADDRESS: string;
  VENUE_2_CITY: string;
  VENUE_2_PERFORMANCE_DATE: string;
  VENUE_2_PERFORMANCE_TIME: string;
  VENUE_2_SOUNDCHECK_TIME: string;
  VENUE_2_PRIMARY_CONTACT_NAME: string;
  VENUE_2_PRIMARY_CONTACT_EMAIL: string;
  VENUE_2_PRIMARY_CONTACT_PHONE: string;
  VENUE_2_TECH_CONTACT_NAME: string;
  VENUE_2_TECH_CONTACT_EMAIL: string;
  VENUE_2_TECH_CONTACT_PHONE: string;
  VENUE_2_ARTIST_LIAISON_NAME: string;
  VENUE_2_ARTIST_LIAISON_EMAIL: string;
  VENUE_2_ARTIST_LIAISON_PHONE: string;
  VENUE_2_TRANSPORT_CONTACT_NAME: string;
  VENUE_2_TRANSPORT_CONTACT_PHONE: string;
  VENUE_2_HOTEL_CONTACT_NAME: string;
  VENUE_2_HOTEL_CONTACT_PHONE: string;
  VENUE_2_CATERING_CONTACT_NAME: string;
  VENUE_2_CATERING_CONTACT_PHONE: string;
  VENUE_2_SECURITY_CONTACT_NAME: string;
  VENUE_2_SECURITY_CONTACT_PHONE: string;
  VENUE_2_MERCHANDISING_CONTACT_NAME: string;
  VENUE_2_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_2_PROMOTER_CONTACT_NAME: string;
  VENUE_2_PROMOTER_CONTACT_EMAIL: string;
  VENUE_2_PROMOTER_CONTACT_PHONE: string;
  VENUE_2_PRODUCTION_CONTACT_NAME: string;
  VENUE_2_PRODUCTION_CONTACT_PHONE: string;
  VENUE_2_MARKETING_CONTACT_NAME: string;
  VENUE_2_MARKETING_CONTACT_EMAIL: string;
  VENUE_2_LUNCH_TYPE: string;
  VENUE_2_LUNCH_LOCATION: string;
  VENUE_2_LUNCH_BUDGET: string;
  VENUE_2_DINNER_TYPE: string;
  VENUE_2_DINNER_LOCATION: string;
  VENUE_2_HOTEL_NAME: string;
  VENUE_2_HOTEL_ADDRESS: string;

  // Venue 3
  VENUE_3_NAME: string;
  VENUE_3_ADDRESS: string;
  VENUE_3_CITY: string;
  VENUE_3_PERFORMANCE_DATE: string;
  VENUE_3_PERFORMANCE_TIME: string;
  VENUE_3_SOUNDCHECK_TIME: string;
  VENUE_3_PRIMARY_CONTACT_NAME: string;
  VENUE_3_PRIMARY_CONTACT_EMAIL: string;
  VENUE_3_PRIMARY_CONTACT_PHONE: string;
  VENUE_3_TECH_CONTACT_NAME: string;
  VENUE_3_TECH_CONTACT_EMAIL: string;
  VENUE_3_TECH_CONTACT_PHONE: string;
  VENUE_3_ARTIST_LIAISON_NAME: string;
  VENUE_3_ARTIST_LIAISON_EMAIL: string;
  VENUE_3_ARTIST_LIAISON_PHONE: string;
  VENUE_3_TRANSPORT_CONTACT_NAME: string;
  VENUE_3_TRANSPORT_CONTACT_PHONE: string;
  VENUE_3_HOTEL_CONTACT_NAME: string;
  VENUE_3_HOTEL_CONTACT_PHONE: string;
  VENUE_3_CATERING_CONTACT_NAME: string;
  VENUE_3_CATERING_CONTACT_PHONE: string;
  VENUE_3_SECURITY_CONTACT_NAME: string;
  VENUE_3_SECURITY_CONTACT_PHONE: string;
  VENUE_3_MERCHANDISING_CONTACT_NAME: string;
  VENUE_3_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_3_PROMOTER_CONTACT_NAME: string;
  VENUE_3_PROMOTER_CONTACT_EMAIL: string;
  VENUE_3_PROMOTER_CONTACT_PHONE: string;
  VENUE_3_PRODUCTION_CONTACT_NAME: string;
  VENUE_3_PRODUCTION_CONTACT_PHONE: string;
  VENUE_3_MARKETING_CONTACT_NAME: string;
  VENUE_3_MARKETING_CONTACT_EMAIL: string;
  VENUE_3_LUNCH_TYPE: string;
  VENUE_3_LUNCH_LOCATION: string;
  VENUE_3_LUNCH_BUDGET: string;
  VENUE_3_DINNER_TYPE: string;
  VENUE_3_DINNER_LOCATION: string;
  VENUE_3_HOTEL_NAME: string;
  VENUE_3_HOTEL_ADDRESS: string;

  // Venue 4
  VENUE_4_NAME: string;
  VENUE_4_ADDRESS: string;
  VENUE_4_CITY: string;
  VENUE_4_PERFORMANCE_DATE: string;
  VENUE_4_PERFORMANCE_TIME: string;
  VENUE_4_SOUNDCHECK_TIME: string;
  VENUE_4_PRIMARY_CONTACT_NAME: string;
  VENUE_4_PRIMARY_CONTACT_EMAIL: string;
  VENUE_4_PRIMARY_CONTACT_PHONE: string;
  VENUE_4_TECH_CONTACT_NAME: string;
  VENUE_4_TECH_CONTACT_EMAIL: string;
  VENUE_4_TECH_CONTACT_PHONE: string;
  VENUE_4_ARTIST_LIAISON_NAME: string;
  VENUE_4_ARTIST_LIAISON_EMAIL: string;
  VENUE_4_ARTIST_LIAISON_PHONE: string;
  VENUE_4_TRANSPORT_CONTACT_NAME: string;
  VENUE_4_TRANSPORT_CONTACT_PHONE: string;
  VENUE_4_HOTEL_CONTACT_NAME: string;
  VENUE_4_HOTEL_CONTACT_PHONE: string;
  VENUE_4_CATERING_CONTACT_NAME: string;
  VENUE_4_CATERING_CONTACT_PHONE: string;
  VENUE_4_SECURITY_CONTACT_NAME: string;
  VENUE_4_SECURITY_CONTACT_PHONE: string;
  VENUE_4_MERCHANDISING_CONTACT_NAME: string;
  VENUE_4_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_4_PROMOTER_CONTACT_NAME: string;
  VENUE_4_PROMOTER_CONTACT_EMAIL: string;
  VENUE_4_PROMOTER_CONTACT_PHONE: string;
  VENUE_4_PRODUCTION_CONTACT_NAME: string;
  VENUE_4_PRODUCTION_CONTACT_PHONE: string;
  VENUE_4_MARKETING_CONTACT_NAME: string;
  VENUE_4_MARKETING_CONTACT_EMAIL: string;
  VENUE_4_LUNCH_TYPE: string;
  VENUE_4_LUNCH_LOCATION: string;
  VENUE_4_LUNCH_BUDGET: string;
  VENUE_4_DINNER_TYPE: string;
  VENUE_4_DINNER_LOCATION: string;
  VENUE_4_HOTEL_NAME: string;
  VENUE_4_HOTEL_ADDRESS: string;

  // Venue 5
  VENUE_5_NAME: string;
  VENUE_5_ADDRESS: string;
  VENUE_5_CITY: string;
  VENUE_5_PERFORMANCE_DATE: string;
  VENUE_5_PERFORMANCE_TIME: string;
  VENUE_5_SOUNDCHECK_TIME: string;
  VENUE_5_PRIMARY_CONTACT_NAME: string;
  VENUE_5_PRIMARY_CONTACT_EMAIL: string;
  VENUE_5_PRIMARY_CONTACT_PHONE: string;
  VENUE_5_TECH_CONTACT_NAME: string;
  VENUE_5_TECH_CONTACT_EMAIL: string;
  VENUE_5_TECH_CONTACT_PHONE: string;
  VENUE_5_ARTIST_LIAISON_NAME: string;
  VENUE_5_ARTIST_LIAISON_EMAIL: string;
  VENUE_5_ARTIST_LIAISON_PHONE: string;
  VENUE_5_TRANSPORT_CONTACT_NAME: string;
  VENUE_5_TRANSPORT_CONTACT_PHONE: string;
  VENUE_5_HOTEL_CONTACT_NAME: string;
  VENUE_5_HOTEL_CONTACT_PHONE: string;
  VENUE_5_CATERING_CONTACT_NAME: string;
  VENUE_5_CATERING_CONTACT_PHONE: string;
  VENUE_5_SECURITY_CONTACT_NAME: string;
  VENUE_5_SECURITY_CONTACT_PHONE: string;
  VENUE_5_MERCHANDISING_CONTACT_NAME: string;
  VENUE_5_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_5_PROMOTER_CONTACT_NAME: string;
  VENUE_5_PROMOTER_CONTACT_EMAIL: string;
  VENUE_5_PROMOTER_CONTACT_PHONE: string;
  VENUE_5_PRODUCTION_CONTACT_NAME: string;
  VENUE_5_PRODUCTION_CONTACT_PHONE: string;
  VENUE_5_MARKETING_CONTACT_NAME: string;
  VENUE_5_MARKETING_CONTACT_EMAIL: string;
  VENUE_5_LUNCH_TYPE: string;
  VENUE_5_LUNCH_LOCATION: string;
  VENUE_5_LUNCH_BUDGET: string;
  VENUE_5_DINNER_TYPE: string;
  VENUE_5_DINNER_LOCATION: string;
  VENUE_5_HOTEL_NAME: string;
  VENUE_5_HOTEL_ADDRESS: string;

  // Venue 6
  VENUE_6_NAME: string;
  VENUE_6_ADDRESS: string;
  VENUE_6_CITY: string;
  VENUE_6_PERFORMANCE_DATE: string;
  VENUE_6_PERFORMANCE_TIME: string;
  VENUE_6_SOUNDCHECK_TIME: string;
  VENUE_6_PRIMARY_CONTACT_NAME: string;
  VENUE_6_PRIMARY_CONTACT_EMAIL: string;
  VENUE_6_PRIMARY_CONTACT_PHONE: string;
  VENUE_6_TECH_CONTACT_NAME: string;
  VENUE_6_TECH_CONTACT_EMAIL: string;
  VENUE_6_TECH_CONTACT_PHONE: string;
  VENUE_6_ARTIST_LIAISON_NAME: string;
  VENUE_6_ARTIST_LIAISON_EMAIL: string;
  VENUE_6_ARTIST_LIAISON_PHONE: string;
  VENUE_6_TRANSPORT_CONTACT_NAME: string;
  VENUE_6_TRANSPORT_CONTACT_PHONE: string;
  VENUE_6_HOTEL_CONTACT_NAME: string;
  VENUE_6_HOTEL_CONTACT_PHONE: string;
  VENUE_6_CATERING_CONTACT_NAME: string;
  VENUE_6_CATERING_CONTACT_PHONE: string;
  VENUE_6_SECURITY_CONTACT_NAME: string;
  VENUE_6_SECURITY_CONTACT_PHONE: string;
  VENUE_6_MERCHANDISING_CONTACT_NAME: string;
  VENUE_6_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_6_PROMOTER_CONTACT_NAME: string;
  VENUE_6_PROMOTER_CONTACT_EMAIL: string;
  VENUE_6_PROMOTER_CONTACT_PHONE: string;
  VENUE_6_PRODUCTION_CONTACT_NAME: string;
  VENUE_6_PRODUCTION_CONTACT_PHONE: string;
  VENUE_6_MARKETING_CONTACT_NAME: string;
  VENUE_6_MARKETING_CONTACT_EMAIL: string;
  VENUE_6_LUNCH_TYPE: string;
  VENUE_6_LUNCH_LOCATION: string;
  VENUE_6_LUNCH_BUDGET: string;
  VENUE_6_DINNER_TYPE: string;
  VENUE_6_DINNER_LOCATION: string;
  VENUE_6_HOTEL_NAME: string;
  VENUE_6_HOTEL_ADDRESS: string;

  // Venue 7
  VENUE_7_NAME: string;
  VENUE_7_ADDRESS: string;
  VENUE_7_CITY: string;
  VENUE_7_PERFORMANCE_DATE: string;
  VENUE_7_PERFORMANCE_TIME: string;
  VENUE_7_SOUNDCHECK_TIME: string;
  VENUE_7_PRIMARY_CONTACT_NAME: string;
  VENUE_7_PRIMARY_CONTACT_EMAIL: string;
  VENUE_7_PRIMARY_CONTACT_PHONE: string;
  VENUE_7_TECH_CONTACT_NAME: string;
  VENUE_7_TECH_CONTACT_EMAIL: string;
  VENUE_7_TECH_CONTACT_PHONE: string;
  VENUE_7_ARTIST_LIAISON_NAME: string;
  VENUE_7_ARTIST_LIAISON_EMAIL: string;
  VENUE_7_ARTIST_LIAISON_PHONE: string;
  VENUE_7_TRANSPORT_CONTACT_NAME: string;
  VENUE_7_TRANSPORT_CONTACT_PHONE: string;
  VENUE_7_HOTEL_CONTACT_NAME: string;
  VENUE_7_HOTEL_CONTACT_PHONE: string;
  VENUE_7_CATERING_CONTACT_NAME: string;
  VENUE_7_CATERING_CONTACT_PHONE: string;
  VENUE_7_SECURITY_CONTACT_NAME: string;
  VENUE_7_SECURITY_CONTACT_PHONE: string;
  VENUE_7_MERCHANDISING_CONTACT_NAME: string;
  VENUE_7_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_7_PROMOTER_CONTACT_NAME: string;
  VENUE_7_PROMOTER_CONTACT_EMAIL: string;
  VENUE_7_PROMOTER_CONTACT_PHONE: string;
  VENUE_7_PRODUCTION_CONTACT_NAME: string;
  VENUE_7_PRODUCTION_CONTACT_PHONE: string;
  VENUE_7_MARKETING_CONTACT_NAME: string;
  VENUE_7_MARKETING_CONTACT_EMAIL: string;
  VENUE_7_LUNCH_TYPE: string;
  VENUE_7_LUNCH_LOCATION: string;
  VENUE_7_LUNCH_BUDGET: string;
  VENUE_7_DINNER_TYPE: string;
  VENUE_7_DINNER_LOCATION: string;
  VENUE_7_HOTEL_NAME: string;
  VENUE_7_HOTEL_ADDRESS: string;

  // Venue 8
  VENUE_8_NAME: string;
  VENUE_8_ADDRESS: string;
  VENUE_8_CITY: string;
  VENUE_8_PERFORMANCE_DATE: string;
  VENUE_8_PERFORMANCE_TIME: string;
  VENUE_8_SOUNDCHECK_TIME: string;
  VENUE_8_PRIMARY_CONTACT_NAME: string;
  VENUE_8_PRIMARY_CONTACT_EMAIL: string;
  VENUE_8_PRIMARY_CONTACT_PHONE: string;
  VENUE_8_TECH_CONTACT_NAME: string;
  VENUE_8_TECH_CONTACT_EMAIL: string;
  VENUE_8_TECH_CONTACT_PHONE: string;
  VENUE_8_ARTIST_LIAISON_NAME: string;
  VENUE_8_ARTIST_LIAISON_EMAIL: string;
  VENUE_8_ARTIST_LIAISON_PHONE: string;
  VENUE_8_TRANSPORT_CONTACT_NAME: string;
  VENUE_8_TRANSPORT_CONTACT_PHONE: string;
  VENUE_8_HOTEL_CONTACT_NAME: string;
  VENUE_8_HOTEL_CONTACT_PHONE: string;
  VENUE_8_CATERING_CONTACT_NAME: string;
  VENUE_8_CATERING_CONTACT_PHONE: string;
  VENUE_8_SECURITY_CONTACT_NAME: string;
  VENUE_8_SECURITY_CONTACT_PHONE: string;
  VENUE_8_MERCHANDISING_CONTACT_NAME: string;
  VENUE_8_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_8_PROMOTER_CONTACT_NAME: string;
  VENUE_8_PROMOTER_CONTACT_EMAIL: string;
  VENUE_8_PROMOTER_CONTACT_PHONE: string;
  VENUE_8_PRODUCTION_CONTACT_NAME: string;
  VENUE_8_PRODUCTION_CONTACT_PHONE: string;
  VENUE_8_MARKETING_CONTACT_NAME: string;
  VENUE_8_MARKETING_CONTACT_EMAIL: string;
  VENUE_8_LUNCH_TYPE: string;
  VENUE_8_LUNCH_LOCATION: string;
  VENUE_8_LUNCH_BUDGET: string;
  VENUE_8_DINNER_TYPE: string;
  VENUE_8_DINNER_LOCATION: string;
  VENUE_8_HOTEL_NAME: string;
  VENUE_8_HOTEL_ADDRESS: string;

  // Venue 9
  VENUE_9_NAME: string;
  VENUE_9_ADDRESS: string;
  VENUE_9_CITY: string;
  VENUE_9_PERFORMANCE_DATE: string;
  VENUE_9_PERFORMANCE_TIME: string;
  VENUE_9_SOUNDCHECK_TIME: string;
  VENUE_9_PRIMARY_CONTACT_NAME: string;
  VENUE_9_PRIMARY_CONTACT_EMAIL: string;
  VENUE_9_PRIMARY_CONTACT_PHONE: string;
  VENUE_9_TECH_CONTACT_NAME: string;
  VENUE_9_TECH_CONTACT_EMAIL: string;
  VENUE_9_TECH_CONTACT_PHONE: string;
  VENUE_9_ARTIST_LIAISON_NAME: string;
  VENUE_9_ARTIST_LIAISON_EMAIL: string;
  VENUE_9_ARTIST_LIAISON_PHONE: string;
  VENUE_9_TRANSPORT_CONTACT_NAME: string;
  VENUE_9_TRANSPORT_CONTACT_PHONE: string;
  VENUE_9_HOTEL_CONTACT_NAME: string;
  VENUE_9_HOTEL_CONTACT_PHONE: string;
  VENUE_9_CATERING_CONTACT_NAME: string;
  VENUE_9_CATERING_CONTACT_PHONE: string;
  VENUE_9_SECURITY_CONTACT_NAME: string;
  VENUE_9_SECURITY_CONTACT_PHONE: string;
  VENUE_9_MERCHANDISING_CONTACT_NAME: string;
  VENUE_9_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_9_PROMOTER_CONTACT_NAME: string;
  VENUE_9_PROMOTER_CONTACT_EMAIL: string;
  VENUE_9_PROMOTER_CONTACT_PHONE: string;
  VENUE_9_PRODUCTION_CONTACT_NAME: string;
  VENUE_9_PRODUCTION_CONTACT_PHONE: string;
  VENUE_9_MARKETING_CONTACT_NAME: string;
  VENUE_9_MARKETING_CONTACT_EMAIL: string;
  VENUE_9_LUNCH_TYPE: string;
  VENUE_9_LUNCH_LOCATION: string;
  VENUE_9_LUNCH_BUDGET: string;
  VENUE_9_DINNER_TYPE: string;
  VENUE_9_DINNER_LOCATION: string;
  VENUE_9_HOTEL_NAME: string;
  VENUE_9_HOTEL_ADDRESS: string;

  // Venue 10
  VENUE_10_NAME: string;
  VENUE_10_ADDRESS: string;
  VENUE_10_CITY: string;
  VENUE_10_PERFORMANCE_DATE: string;
  VENUE_10_PERFORMANCE_TIME: string;
  VENUE_10_SOUNDCHECK_TIME: string;
  VENUE_10_PRIMARY_CONTACT_NAME: string;
  VENUE_10_PRIMARY_CONTACT_EMAIL: string;
  VENUE_10_PRIMARY_CONTACT_PHONE: string;
  VENUE_10_TECH_CONTACT_NAME: string;
  VENUE_10_TECH_CONTACT_EMAIL: string;
  VENUE_10_TECH_CONTACT_PHONE: string;
  VENUE_10_ARTIST_LIAISON_NAME: string;
  VENUE_10_ARTIST_LIAISON_EMAIL: string;
  VENUE_10_ARTIST_LIAISON_PHONE: string;
  VENUE_10_TRANSPORT_CONTACT_NAME: string;
  VENUE_10_TRANSPORT_CONTACT_PHONE: string;
  VENUE_10_HOTEL_CONTACT_NAME: string;
  VENUE_10_HOTEL_CONTACT_PHONE: string;
  VENUE_10_CATERING_CONTACT_NAME: string;
  VENUE_10_CATERING_CONTACT_PHONE: string;
  VENUE_10_SECURITY_CONTACT_NAME: string;
  VENUE_10_SECURITY_CONTACT_PHONE: string;
  VENUE_10_MERCHANDISING_CONTACT_NAME: string;
  VENUE_10_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_10_PROMOTER_CONTACT_NAME: string;
  VENUE_10_PROMOTER_CONTACT_EMAIL: string;
  VENUE_10_PROMOTER_CONTACT_PHONE: string;
  VENUE_10_PRODUCTION_CONTACT_NAME: string;
  VENUE_10_PRODUCTION_CONTACT_PHONE: string;
  VENUE_10_MARKETING_CONTACT_NAME: string;
  VENUE_10_MARKETING_CONTACT_EMAIL: string;
  VENUE_10_LUNCH_TYPE: string;
  VENUE_10_LUNCH_LOCATION: string;
  VENUE_10_LUNCH_BUDGET: string;
  VENUE_10_DINNER_TYPE: string;
  VENUE_10_DINNER_LOCATION: string;
  VENUE_10_HOTEL_NAME: string;
  VENUE_10_HOTEL_ADDRESS: string;

  // Venue 11
  VENUE_11_NAME: string;
  VENUE_11_ADDRESS: string;
  VENUE_11_CITY: string;
  VENUE_11_PERFORMANCE_DATE: string;
  VENUE_11_PERFORMANCE_TIME: string;
  VENUE_11_SOUNDCHECK_TIME: string;
  VENUE_11_PRIMARY_CONTACT_NAME: string;
  VENUE_11_PRIMARY_CONTACT_EMAIL: string;
  VENUE_11_PRIMARY_CONTACT_PHONE: string;
  VENUE_11_TECH_CONTACT_NAME: string;
  VENUE_11_TECH_CONTACT_EMAIL: string;
  VENUE_11_TECH_CONTACT_PHONE: string;
  VENUE_11_ARTIST_LIAISON_NAME: string;
  VENUE_11_ARTIST_LIAISON_EMAIL: string;
  VENUE_11_ARTIST_LIAISON_PHONE: string;
  VENUE_11_TRANSPORT_CONTACT_NAME: string;
  VENUE_11_TRANSPORT_CONTACT_PHONE: string;
  VENUE_11_HOTEL_CONTACT_NAME: string;
  VENUE_11_HOTEL_CONTACT_PHONE: string;
  VENUE_11_CATERING_CONTACT_NAME: string;
  VENUE_11_CATERING_CONTACT_PHONE: string;
  VENUE_11_SECURITY_CONTACT_NAME: string;
  VENUE_11_SECURITY_CONTACT_PHONE: string;
  VENUE_11_MERCHANDISING_CONTACT_NAME: string;
  VENUE_11_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_11_PROMOTER_CONTACT_NAME: string;
  VENUE_11_PROMOTER_CONTACT_EMAIL: string;
  VENUE_11_PROMOTER_CONTACT_PHONE: string;
  VENUE_11_PRODUCTION_CONTACT_NAME: string;
  VENUE_11_PRODUCTION_CONTACT_PHONE: string;
  VENUE_11_MARKETING_CONTACT_NAME: string;
  VENUE_11_MARKETING_CONTACT_EMAIL: string;
  VENUE_11_LUNCH_TYPE: string;
  VENUE_11_LUNCH_LOCATION: string;
  VENUE_11_LUNCH_BUDGET: string;
  VENUE_11_DINNER_TYPE: string;
  VENUE_11_DINNER_LOCATION: string;
  VENUE_11_HOTEL_NAME: string;
  VENUE_11_HOTEL_ADDRESS: string;

  // Venue 12
  VENUE_12_NAME: string;
  VENUE_12_ADDRESS: string;
  VENUE_12_CITY: string;
  VENUE_12_PERFORMANCE_DATE: string;
  VENUE_12_PERFORMANCE_TIME: string;
  VENUE_12_SOUNDCHECK_TIME: string;
  VENUE_12_PRIMARY_CONTACT_NAME: string;
  VENUE_12_PRIMARY_CONTACT_EMAIL: string;
  VENUE_12_PRIMARY_CONTACT_PHONE: string;
  VENUE_12_TECH_CONTACT_NAME: string;
  VENUE_12_TECH_CONTACT_EMAIL: string;
  VENUE_12_TECH_CONTACT_PHONE: string;
  VENUE_12_ARTIST_LIAISON_NAME: string;
  VENUE_12_ARTIST_LIAISON_EMAIL: string;
  VENUE_12_ARTIST_LIAISON_PHONE: string;
  VENUE_12_TRANSPORT_CONTACT_NAME: string;
  VENUE_12_TRANSPORT_CONTACT_PHONE: string;
  VENUE_12_HOTEL_CONTACT_NAME: string;
  VENUE_12_HOTEL_CONTACT_PHONE: string;
  VENUE_12_CATERING_CONTACT_NAME: string;
  VENUE_12_CATERING_CONTACT_PHONE: string;
  VENUE_12_SECURITY_CONTACT_NAME: string;
  VENUE_12_SECURITY_CONTACT_PHONE: string;
  VENUE_12_MERCHANDISING_CONTACT_NAME: string;
  VENUE_12_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_12_PROMOTER_CONTACT_NAME: string;
  VENUE_12_PROMOTER_CONTACT_EMAIL: string;
  VENUE_12_PROMOTER_CONTACT_PHONE: string;
  VENUE_12_PRODUCTION_CONTACT_NAME: string;
  VENUE_12_PRODUCTION_CONTACT_PHONE: string;
  VENUE_12_MARKETING_CONTACT_NAME: string;
  VENUE_12_MARKETING_CONTACT_EMAIL: string;
  VENUE_12_LUNCH_TYPE: string;
  VENUE_12_LUNCH_LOCATION: string;
  VENUE_12_LUNCH_BUDGET: string;
  VENUE_12_DINNER_TYPE: string;
  VENUE_12_DINNER_LOCATION: string;
  VENUE_12_HOTEL_NAME: string;
  VENUE_12_HOTEL_ADDRESS: string;

  // Venue 13
  VENUE_13_NAME: string;
  VENUE_13_ADDRESS: string;
  VENUE_13_CITY: string;
  VENUE_13_PERFORMANCE_DATE: string;
  VENUE_13_PERFORMANCE_TIME: string;
  VENUE_13_SOUNDCHECK_TIME: string;
  VENUE_13_PRIMARY_CONTACT_NAME: string;
  VENUE_13_PRIMARY_CONTACT_EMAIL: string;
  VENUE_13_PRIMARY_CONTACT_PHONE: string;
  VENUE_13_TECH_CONTACT_NAME: string;
  VENUE_13_TECH_CONTACT_EMAIL: string;
  VENUE_13_TECH_CONTACT_PHONE: string;
  VENUE_13_ARTIST_LIAISON_NAME: string;
  VENUE_13_ARTIST_LIAISON_EMAIL: string;
  VENUE_13_ARTIST_LIAISON_PHONE: string;
  VENUE_13_TRANSPORT_CONTACT_NAME: string;
  VENUE_13_TRANSPORT_CONTACT_PHONE: string;
  VENUE_13_HOTEL_CONTACT_NAME: string;
  VENUE_13_HOTEL_CONTACT_PHONE: string;
  VENUE_13_CATERING_CONTACT_NAME: string;
  VENUE_13_CATERING_CONTACT_PHONE: string;
  VENUE_13_SECURITY_CONTACT_NAME: string;
  VENUE_13_SECURITY_CONTACT_PHONE: string;
  VENUE_13_MERCHANDISING_CONTACT_NAME: string;
  VENUE_13_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_13_PROMOTER_CONTACT_NAME: string;
  VENUE_13_PROMOTER_CONTACT_EMAIL: string;
  VENUE_13_PROMOTER_CONTACT_PHONE: string;
  VENUE_13_PRODUCTION_CONTACT_NAME: string;
  VENUE_13_PRODUCTION_CONTACT_PHONE: string;
  VENUE_13_MARKETING_CONTACT_NAME: string;
  VENUE_13_MARKETING_CONTACT_EMAIL: string;
  VENUE_13_LUNCH_TYPE: string;
  VENUE_13_LUNCH_LOCATION: string;
  VENUE_13_LUNCH_BUDGET: string;
  VENUE_13_DINNER_TYPE: string;
  VENUE_13_DINNER_LOCATION: string;
  VENUE_13_HOTEL_NAME: string;
  VENUE_13_HOTEL_ADDRESS: string;

  // Venue 14
  VENUE_14_NAME: string;
  VENUE_14_ADDRESS: string;
  VENUE_14_CITY: string;
  VENUE_14_PERFORMANCE_DATE: string;
  VENUE_14_PERFORMANCE_TIME: string;
  VENUE_14_SOUNDCHECK_TIME: string;
  VENUE_14_PRIMARY_CONTACT_NAME: string;
  VENUE_14_PRIMARY_CONTACT_EMAIL: string;
  VENUE_14_PRIMARY_CONTACT_PHONE: string;
  VENUE_14_TECH_CONTACT_NAME: string;
  VENUE_14_TECH_CONTACT_EMAIL: string;
  VENUE_14_TECH_CONTACT_PHONE: string;
  VENUE_14_ARTIST_LIAISON_NAME: string;
  VENUE_14_ARTIST_LIAISON_EMAIL: string;
  VENUE_14_ARTIST_LIAISON_PHONE: string;
  VENUE_14_TRANSPORT_CONTACT_NAME: string;
  VENUE_14_TRANSPORT_CONTACT_PHONE: string;
  VENUE_14_HOTEL_CONTACT_NAME: string;
  VENUE_14_HOTEL_CONTACT_PHONE: string;
  VENUE_14_CATERING_CONTACT_NAME: string;
  VENUE_14_CATERING_CONTACT_PHONE: string;
  VENUE_14_SECURITY_CONTACT_NAME: string;
  VENUE_14_SECURITY_CONTACT_PHONE: string;
  VENUE_14_MERCHANDISING_CONTACT_NAME: string;
  VENUE_14_MERCHANDISING_CONTACT_PHONE: string;
  VENUE_14_PROMOTER_CONTACT_NAME: string;
  VENUE_14_PROMOTER_CONTACT_EMAIL: string;
  VENUE_14_PROMOTER_CONTACT_PHONE: string;
  VENUE_14_PRODUCTION_CONTACT_NAME: string;
  VENUE_14_PRODUCTION_CONTACT_PHONE: string;
  VENUE_14_MARKETING_CONTACT_NAME: string;
  VENUE_14_MARKETING_CONTACT_EMAIL: string;
  VENUE_14_LUNCH_TYPE: string;
  VENUE_14_LUNCH_LOCATION: string;
  VENUE_14_LUNCH_BUDGET: string;
  VENUE_14_DINNER_TYPE: string;
  VENUE_14_DINNER_LOCATION: string;
  VENUE_14_HOTEL_NAME: string;
  VENUE_14_HOTEL_ADDRESS: string;

  // ========== TRANSPORT ROUTES BETWEEN ALL VENUES ==========
  // This is the KEY missing piece - route-specific transport variables!
  
  // Route 1: Venue 1 → Venue 2
  VENUE_1_TO_VENUE_2_TRANSPORT_TYPE: string; // flight, train, rental_car, etc.
  VENUE_1_TO_VENUE_2_DEPARTURE_TIME: string; // HH:MM
  VENUE_1_TO_VENUE_2_ARRIVAL_TIME: string;   // HH:MM
  VENUE_1_TO_VENUE_2_DETAILS: string;        // "Avis rental pickup Dublin Airport" or "Ryanair FR123"

  // Route 2: Venue 2 → Venue 3
  VENUE_2_TO_VENUE_3_TRANSPORT_TYPE: string;
  VENUE_2_TO_VENUE_3_DEPARTURE_TIME: string;
  VENUE_2_TO_VENUE_3_ARRIVAL_TIME: string;
  VENUE_2_TO_VENUE_3_DETAILS: string;

  // Route 3: Venue 3 → Venue 4
  VENUE_3_TO_VENUE_4_TRANSPORT_TYPE: string;
  VENUE_3_TO_VENUE_4_DEPARTURE_TIME: string;
  VENUE_3_TO_VENUE_4_ARRIVAL_TIME: string;
  VENUE_3_TO_VENUE_4_DETAILS: string;

  // Route 4: Venue 4 → Venue 5
  VENUE_4_TO_VENUE_5_TRANSPORT_TYPE: string;
  VENUE_4_TO_VENUE_5_DEPARTURE_TIME: string;
  VENUE_4_TO_VENUE_5_ARRIVAL_TIME: string;
  VENUE_4_TO_VENUE_5_DETAILS: string;

  // Route 5: Venue 5 → Venue 6
  VENUE_5_TO_VENUE_6_TRANSPORT_TYPE: string;
  VENUE_5_TO_VENUE_6_DEPARTURE_TIME: string;
  VENUE_5_TO_VENUE_6_ARRIVAL_TIME: string;
  VENUE_5_TO_VENUE_6_DETAILS: string;

  // Route 6: Venue 6 → Venue 7
  VENUE_6_TO_VENUE_7_TRANSPORT_TYPE: string;
  VENUE_6_TO_VENUE_7_DEPARTURE_TIME: string;
  VENUE_6_TO_VENUE_7_ARRIVAL_TIME: string;
  VENUE_6_TO_VENUE_7_DETAILS: string;

  // Route 7: Venue 7 → Venue 8
  VENUE_7_TO_VENUE_8_TRANSPORT_TYPE: string;
  VENUE_7_TO_VENUE_8_DEPARTURE_TIME: string;
  VENUE_7_TO_VENUE_8_ARRIVAL_TIME: string;
  VENUE_7_TO_VENUE_8_DETAILS: string;

  // Route 8: Venue 8 → Venue 9
  VENUE_8_TO_VENUE_9_TRANSPORT_TYPE: string;
  VENUE_8_TO_VENUE_9_DEPARTURE_TIME: string;
  VENUE_8_TO_VENUE_9_ARRIVAL_TIME: string;
  VENUE_8_TO_VENUE_9_DETAILS: string;

  // Route 9: Venue 9 → Venue 10
  VENUE_9_TO_VENUE_10_TRANSPORT_TYPE: string;
  VENUE_9_TO_VENUE_10_DEPARTURE_TIME: string;
  VENUE_9_TO_VENUE_10_ARRIVAL_TIME: string;
  VENUE_9_TO_VENUE_10_DETAILS: string;

  // Route 10: Venue 10 → Venue 11
  VENUE_10_TO_VENUE_11_TRANSPORT_TYPE: string;
  VENUE_10_TO_VENUE_11_DEPARTURE_TIME: string;
  VENUE_10_TO_VENUE_11_ARRIVAL_TIME: string;
  VENUE_10_TO_VENUE_11_DETAILS: string;

  // Route 11: Venue 11 → Venue 12
  VENUE_11_TO_VENUE_12_TRANSPORT_TYPE: string;
  VENUE_11_TO_VENUE_12_DEPARTURE_TIME: string;
  VENUE_11_TO_VENUE_12_ARRIVAL_TIME: string;
  VENUE_11_TO_VENUE_12_DETAILS: string;

  // Route 12: Venue 12 → Venue 13
  VENUE_12_TO_VENUE_13_TRANSPORT_TYPE: string;
  VENUE_12_TO_VENUE_13_DEPARTURE_TIME: string;
  VENUE_12_TO_VENUE_13_ARRIVAL_TIME: string;
  VENUE_12_TO_VENUE_13_DETAILS: string;

  // Route 13: Venue 13 → Venue 14
  VENUE_13_TO_VENUE_14_TRANSPORT_TYPE: string;
  VENUE_13_TO_VENUE_14_DEPARTURE_TIME: string;
  VENUE_13_TO_VENUE_14_ARRIVAL_TIME: string;
  VENUE_13_TO_VENUE_14_DETAILS: string;
}

// Complete tour template structure
export interface TourTemplate {
  id: string;
  name: string;
  description: string;
  tourType: 'club_tour' | 'festival_circuit' | 'arena_tour' | 'international_tour';
  duration: number; // total days
  numberOfShows: number;
  venues: TourVenue[];
  days: TourDay[];
  estimatedDistance?: number; // total km
  estimatedCost?: number;
}

// Options for tour generation
export interface TourGenerationOptions {
  includeRestDays: boolean;
  includeLoadInDays: boolean;
  autoCalculateTravel: boolean;
  maxTravelHoursPerDay: number; // if travel > this, split into travel-only day
  
  defaultMealTimes: {
    breakfast: string; // HH:MM
    lunch: string; // HH:MM
    dinner: string; // HH:MM
  };
  
  defaultSoundcheckDuration: number; // minutes
  defaultPerformanceDuration: number; // minutes
  defaultLoadInDuration: number; // minutes
}

// Helper type for generating venue field keys dynamically
export type VenueFieldPrefix = 
  | 'NAME' | 'ADDRESS' | 'CITY'
  | 'PERFORMANCE_DATE' | 'PERFORMANCE_TIME' | 'SOUNDCHECK_TIME'
  | 'PRIMARY_CONTACT_NAME' | 'PRIMARY_CONTACT_EMAIL' | 'PRIMARY_CONTACT_PHONE'
  | 'TECH_CONTACT_NAME' | 'TECH_CONTACT_EMAIL' | 'TECH_CONTACT_PHONE'
  | 'ARTIST_LIAISON_NAME' | 'ARTIST_LIAISON_EMAIL' | 'ARTIST_LIAISON_PHONE'
  | 'TRANSPORT_CONTACT_NAME' | 'TRANSPORT_CONTACT_PHONE'
  | 'HOTEL_CONTACT_NAME' | 'HOTEL_CONTACT_PHONE'
  | 'CATERING_CONTACT_NAME' | 'CATERING_CONTACT_PHONE'
  | 'SECURITY_CONTACT_NAME' | 'SECURITY_CONTACT_PHONE'
  | 'MERCHANDISING_CONTACT_NAME' | 'MERCHANDISING_CONTACT_PHONE'
  | 'PROMOTER_CONTACT_NAME' | 'PROMOTER_CONTACT_EMAIL' | 'PROMOTER_CONTACT_PHONE'
  | 'PRODUCTION_CONTACT_NAME' | 'PRODUCTION_CONTACT_PHONE'
  | 'MARKETING_CONTACT_NAME' | 'MARKETING_CONTACT_EMAIL'
  | 'LUNCH_TYPE' | 'LUNCH_LOCATION' | 'LUNCH_BUDGET'
  | 'DINNER_TYPE' | 'DINNER_LOCATION'
  | 'HOTEL_NAME' | 'HOTEL_ADDRESS';

// Route field prefixes for transport between venues
export type RouteFieldPrefix = 
  | 'TRANSPORT_TYPE' | 'DEPARTURE_TIME' | 'ARRIVAL_TIME' | 'DETAILS';

// Utility type for venue field names (VENUE_1_NAME, VENUE_2_NAME, etc.)
export type VenueFieldName<N extends number, F extends VenueFieldPrefix> = `VENUE_${N}_${F}`;

// Utility type for route field names (VENUE_1_TO_VENUE_2_TRANSPORT_TYPE, etc.)
export type RouteFieldName<From extends number, To extends number, F extends RouteFieldPrefix> = 
  `VENUE_${From}_TO_VENUE_${To}_${F}`;

// Route information between venues
export interface TourRoute {
  fromVenue: number;
  toVenue: number;
  fromCity: string;
  toCity: string;
  transportType: TransportType;
  departureTime?: string;
  arrivalTime?: string;
  details?: string; // booking details, pickup locations, etc.
  estimatedDuration: number; // hours
  estimatedDistance?: number; // km
  requiresOvernightTravel: boolean; // if true, needs travel-only day
}

export default TourVariables;